import { useState, useEffect } from 'react';
export interface UseIsActiveParams {
  initVal: boolean;
  activateDelay?: number;
  deactivateDelay?: number;
}

export interface UseIsActiveResult {
  isActive: boolean;
  activate: () => void;
  deactivate: () => void;
  isActivationPending: boolean;
  isDeactivationPending: boolean;
}

const MILLISECONDS_IN_A_SECOND = 1000;

export const useIsActive = ({
  initVal,
  activateDelay = 0,
  deactivateDelay = 0,
}: UseIsActiveParams): UseIsActiveResult => {
  const [isActive, setIsActive] = useState<boolean>(initVal);
  const [activateTimeoutId, setActivateTimeoutId] = useState<
    NodeJS.Timeout | number | undefined
  >();

  const [deactivateTimeoutId, setDeactivateTimeoutId] = useState<
    NodeJS.Timeout | number | undefined
  >();

  useEffect(() => {
    return () => {
      clearTimeout(activateTimeoutId);
      clearTimeout(deactivateTimeoutId);
    };
  }, []);

  const activate = () => {
    clearTimeout(deactivateTimeoutId);
    setDeactivateTimeoutId(undefined);
    setActivateTimeoutId(undefined);
    setIsActive(true);
  };

  const deactivate = () => {
    clearTimeout(activateTimeoutId);
    setActivateTimeoutId(undefined);
    setDeactivateTimeoutId(undefined);
    setIsActive(false);
  };

  const activateWithDelay = (delay: number) => {
    clearTimeout(deactivateTimeoutId);
    setDeactivateTimeoutId(undefined);

    if (isActive) {
      clearTimeout(activateTimeoutId);
      return setActivateTimeoutId(undefined);
    }

    if (activateTimeoutId) return;

    const timeoutId = setTimeout(activate, delay * MILLISECONDS_IN_A_SECOND);
    setActivateTimeoutId(timeoutId);
  };

  const deactivateWithDelay = (delay: number) => {
    clearTimeout(activateTimeoutId);
    setActivateTimeoutId(undefined);

    if (!isActive) {
      clearTimeout(deactivateTimeoutId);
      return setDeactivateTimeoutId(undefined);
    }

    if (deactivateTimeoutId) return;

    const timeoutId = setTimeout(deactivate, delay * MILLISECONDS_IN_A_SECOND);
    setDeactivateTimeoutId(timeoutId);
  };

  const triggerActivate = () => {
    if (activateDelay > 0) {
      activateWithDelay(activateDelay);
    } else {
      activate();
    }
  };

  const triggerDeactivate = () => {
    if (deactivateDelay > 0) {
      deactivateWithDelay(deactivateDelay);
    } else {
      deactivate();
    }
  };

  return {
    isActive,
    activate: triggerActivate,
    deactivate: triggerDeactivate,
    isActivationPending: !!activateTimeoutId,
    isDeactivationPending: !!deactivateTimeoutId,
  };
};
